import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Receipt } from "lucide-react";
import { inr, isoToLocal, localToIso, openPdfPath } from "./wardUtils";

const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

export function ChargesTable({ items, onChange }) {
  const upd = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => onChange([...items, { name: "", unit: "unit", qty: 1, rate: 0 }]);
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <section className="rounded-xl border border-border overflow-hidden" data-testid="ward-charges">
      <div className="flex items-center justify-between px-5 py-3 bg-muted/40">
        <h2 className="font-medium">Charges</h2>
        <Button size="sm" variant="outline" onClick={add} data-testid="ward-add-charge"><Plus size={14} className="mr-1" /> Add charge</Button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-muted-foreground">
          <tr><th className="text-left px-4 py-2">Particulars</th><th className="text-left px-2 py-2 w-28">Unit</th><th className="text-right px-2 py-2 w-24">Qty</th><th className="text-right px-2 py-2 w-32">Rate (₹)</th><th className="text-right px-4 py-2 w-32">Amount</th><th className="w-10"></th></tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} className="border-t border-border" data-testid={`ward-charge-row-${i}`}>
              <td className="px-4 py-1.5"><Input value={it.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="e.g. Nebulisation" data-testid={`ward-charge-name-${i}`} className="h-9" /></td>
              <td className="px-2 py-1.5"><Input value={it.unit} onChange={(e) => upd(i, "unit", e.target.value)} placeholder="day / hour" data-testid={`ward-charge-unit-${i}`} className="h-9" /></td>
              <td className="px-2 py-1.5"><Input type="number" min="0" step="any" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} data-testid={`ward-charge-qty-${i}`} className="h-9 text-right" /></td>
              <td className="px-2 py-1.5"><Input type="number" min="0" step="any" value={it.rate} onChange={(e) => upd(i, "rate", e.target.value)} data-testid={`ward-charge-rate-${i}`} className="h-9 text-right" /></td>
              <td className="px-4 py-1.5 text-right tabular font-medium" data-testid={`ward-charge-amount-${i}`}>{inr(num(it.qty) * num(it.rate))}</td>
              <td className="pr-2"><Button variant="ghost" size="icon" className="text-red-500 h-8 w-8" onClick={() => del(i)} data-testid={`ward-charge-del-${i}`}><Trash2 size={14} /></Button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No charges. Click "Add charge".</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

export function PaymentsTable({ payments, onChange, billId }) {
  const upd = (i, k, v) => onChange(payments.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const add = () => onChange([...payments, { at: new Date().toISOString(), amount: 0, method: "cash", reference: "" }]);
  const del = (i) => onChange(payments.filter((_, idx) => idx !== i));
  return (
    <section className="rounded-xl border border-border overflow-hidden" data-testid="ward-payments">
      <div className="flex items-center justify-between px-5 py-3 bg-muted/40">
        <h2 className="font-medium">Payments / Advance</h2>
        <Button size="sm" variant="outline" onClick={add} data-testid="ward-add-payment"><Plus size={14} className="mr-1" /> Add payment</Button>
      </div>
      {payments.length === 0 ? <div className="px-4 py-5 text-sm text-muted-foreground">No payments recorded.</div> : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-2">Date & time</th><th className="text-left px-2 py-2 w-28">Method</th><th className="text-left px-2 py-2">Reference</th><th className="text-right px-2 py-2 w-32">Amount (₹)</th><th className="w-20"></th></tr>
          </thead>
          <tbody>
            {payments.map((p, i) => (
              <tr key={i} className="border-t border-border" data-testid={`ward-payment-row-${i}`}>
                <td className="px-4 py-1.5"><Input type="datetime-local" value={isoToLocal(p.at)} onChange={(e) => upd(i, "at", localToIso(e.target.value))} className="h-9" data-testid={`ward-payment-at-${i}`} /></td>
                <td className="px-2 py-1.5">
                  <select value={p.method} onChange={(e) => upd(i, "method", e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid={`ward-payment-method-${i}`}>
                    {["cash", "upi", "card", "cheque", "bank"].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                  </select>
                </td>
                <td className="px-2 py-1.5"><Input value={p.reference || ""} onChange={(e) => upd(i, "reference", e.target.value)} className="h-9" placeholder="Txn / receipt ref" data-testid={`ward-payment-ref-${i}`} /></td>
                <td className="px-2 py-1.5"><Input type="number" min="0" step="any" value={p.amount} onChange={(e) => upd(i, "amount", e.target.value)} className="h-9 text-right" data-testid={`ward-payment-amount-${i}`} /></td>
                <td className="pr-2 whitespace-nowrap">
                  {billId && p.id && <Button variant="ghost" size="icon" className="h-8 w-8" title="Print receipt" onClick={() => openPdfPath(`/ward/bills/${billId}/payments/${p.id}/receipt`)} data-testid={`ward-payment-receipt-${i}`}><Receipt size={14} /></Button>}
                  <Button variant="ghost" size="icon" className="text-red-500 h-8 w-8" onClick={() => del(i)} data-testid={`ward-payment-del-${i}`}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export function TotalsCard({ bill, onDiscount }) {
  const charges = bill.items.reduce((s, i) => s + num(i.qty) * num(i.rate), 0);
  const discount = num(bill.discount);
  const net = Math.max(charges - discount, 0);
  const paid = bill.payments.reduce((s, p) => s + num(p.amount), 0);
  const balance = net - paid;
  const Row = ({ l, v, bold, warn, tid }) => (
    <div className={`flex justify-between ${bold ? "font-semibold text-base" : "text-sm"} ${warn ? "text-amber-600" : ""}`}><span className="text-muted-foreground">{l}</span><span className="tabular" data-testid={tid}>{v}</span></div>
  );
  return (
    <aside className="rounded-xl border border-border p-5 space-y-3 h-fit" data-testid="ward-totals">
      <Row l="Total charges" v={inr(charges)} tid="ward-total-charges" />
      <div className="flex items-center justify-between gap-3 text-sm">
        <Label className="text-muted-foreground">Discount (₹)</Label>
        <Input type="number" min="0" step="any" value={bill.discount} onChange={(e) => onDiscount(e.target.value)} className="h-9 w-32 text-right" data-testid="ward-discount" />
      </div>
      <div className="border-t border-border pt-3 space-y-2">
        <Row l="Net payable" v={inr(net)} bold tid="ward-net" />
        <Row l="Paid / advance" v={`- ${inr(paid)}`} tid="ward-paid" />
        <Row l="Balance due" v={inr(balance)} bold warn={balance > 0} tid="ward-balance" />
      </div>
    </aside>
  );
}
